import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Users, Lock, LogOut } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../backend';
import type { GuestbookMessage, Rsvp } from '../backend';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const ADMIN_STORAGE_KEY = 'mi-boda-admin';

export function AdminView() {
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [messages, setMessages] = useState<GuestbookMessage[]>([]);
  const [rsvps, setRsvps] = useState<Rsvp[]>([]);
  const [loading, setLoading] = useState(true);

  const secret = process.env.REACT_APP_ADMIN_SECRET;

  useEffect(() => {
    const stored = sessionStorage.getItem(ADMIN_STORAGE_KEY);
    if (stored === 'ok' && secret) setUnlocked(true);
  }, [secret]);

  useEffect(() => {
    if (!unlocked || !isSupabaseConfigured() || !supabase) return;
    setLoading(true);
    Promise.all([
      supabase.from('guestbook_messages').select('id, name, message, created_at').order('created_at', { ascending: false }),
      supabase.from('rsvps').select('id, guest_name, email, attending, num_guests, message, created_at').order('created_at', { ascending: false }),
    ]).then(([msgRes, rsvpRes]) => {
      setMessages((msgRes.data as GuestbookMessage[]) || []);
      setRsvps((rsvpRes.data as Rsvp[]) || []);
    }).finally(() => setLoading(false));
  }, [unlocked]);

  const handleUnlock = (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    if (!secret) {
      setAuthError('error de configuracion');
      return;
    }
    if (password === secret) {
      sessionStorage.setItem(ADMIN_STORAGE_KEY, 'ok');
      setUnlocked(true);
      setPassword('');
    } else {
      setAuthError('Clave incorrecta');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(ADMIN_STORAGE_KEY);
    setUnlocked(false);
  };

  if (!secret) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: 'var(--butter)' }}>
        <div className="max-w-md w-full text-center space-y-4">
          <p className="text-lg" style={{ color: 'var(--emerald)' }}>
           error
          </p>
          <Link to="/" className="inline-block mt-4 underline" style={{ color: 'var(--chartreuse)' }}>Volver a la invitación</Link>
        </div>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: 'var(--butter)' }}>
        <div className="max-w-sm w-full rounded-xl p-8 border" style={{ borderColor: 'rgba(186, 159, 56, 0.3)', backgroundColor: 'var(--pale-lime)' }}>
          <div className="flex items-center justify-center gap-2 mb-6">
            <Lock className="w-8 h-8" style={{ color: 'var(--emerald)' }} />
            <h1 className="text-xl font-serif tracking-widest" style={{ color: 'var(--emerald)' }}>ADMIN</h1>
          </div>
          <form onSubmit={handleUnlock} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Clave de administrador"
              className="w-full px-4 py-3 rounded-lg border bg-white/80"
              style={{ borderColor: 'rgba(186, 159, 56, 0.4)' }}
              autoFocus
            />
            {authError && <p className="text-sm text-red-600">{authError}</p>}
            <button
              type="submit"
              className="w-full py-3 rounded-full border font-serif"
              style={{ borderColor: 'var(--chartreuse)', color: 'var(--emerald)' }}
            >
              Entrar
            </button>
          </form>
          <Link to="/" className="block text-center mt-4 text-sm underline" style={{ color: 'var(--emerald)', opacity: 0.8 }}>Volver a la invitación</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 md:px-8" style={{ backgroundColor: 'var(--butter)' }}>
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <h1 className="text-2xl font-serif tracking-widest" style={{ color: 'var(--emerald)' }}>Panel de administración</h1>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm underline" style={{ color: 'var(--emerald)' }}>Ver invitación</Link>
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm"
              style={{ color: 'var(--emerald)' }}
            >
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>

        {!isSupabaseConfigured() ? (
          <p style={{ color: 'var(--emerald)' }}>Configura Supabase para ver los datos.</p>
        ) : (
          <Tabs defaultValue="messages" className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2 mb-6" style={{ backgroundColor: 'var(--pale-lime)' }}>
              <TabsTrigger value="messages" className="data-[state=active]:bg-[var(--butter)]">
                <MessageCircle className="w-4 h-4 mr-2" /> Mensajes ({messages.length})
              </TabsTrigger>
              <TabsTrigger value="rsvps" className="data-[state=active]:bg-[var(--butter)]">
                <Users className="w-4 h-4 mr-2" /> Confirmaciones ({rsvps.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="messages" className="mt-0">
              {loading ? (
                <p style={{ color: 'var(--emerald)' }}>Cargando mensajes...</p>
              ) : (
                <div className="space-y-4">
                  {messages.length === 0 ? (
                    <p style={{ color: 'var(--emerald)', opacity: 0.8 }}>Aún no hay mensajes.</p>
                  ) : (
                    messages.map((m) => (
                      <div
                        key={m.id}
                        className="rounded-lg p-4 border bg-white/70"
                        style={{ borderColor: 'rgba(186, 159, 56, 0.3)' }}
                      >
                        <div className="flex justify-between items-start gap-2 mb-2">
                          <span className="font-medium" style={{ color: 'var(--emerald)' }}>{m.name}</span>
                          <span className="text-xs opacity-70" style={{ color: 'var(--emerald)' }}>
                            {new Date(m.created_at).toLocaleString('es-MX')}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--emerald)', opacity: 0.9 }}>{m.message}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="rsvps" className="mt-0">
              {loading ? (
                <p style={{ color: 'var(--emerald)' }}>Cargando confirmaciones...</p>
              ) : (
                <div className="space-y-4">
                  {rsvps.length === 0 ? (
                    <p style={{ color: 'var(--emerald)', opacity: 0.8 }}>Aún no hay confirmaciones.</p>
                  ) : (
                    rsvps.map((r) => (
                      <div
                        key={r.id}
                        className="rounded-lg p-4 border bg-white/70"
                        style={{ borderColor: 'rgba(186, 159, 56, 0.3)' }}
                      >
                        <div className="flex flex-wrap justify-between items-start gap-2 mb-2">
                          <span className="font-medium" style={{ color: 'var(--emerald)' }}>{r.guest_name}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: r.attending ? 'var(--pale-lime)' : 'rgba(0,0,0,0.1)', color: 'var(--emerald)' }}>
                            {r.attending ? 'Asiste' : 'No asiste'}
                          </span>
                        </div>
                        <div className="text-sm space-y-1" style={{ color: 'var(--emerald)', opacity: 0.9 }}>
                          {r.email && <p>Email: {r.email}</p>}
                          {r.attending && <p>Acompañantes: {r.num_guests}</p>}
                          {r.message && <p className="mt-2">Comentario: {r.message}</p>}
                          <p className="text-xs opacity-70">{new Date(r.created_at).toLocaleString('es-MX')}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
